import { createSelector } from '@reduxjs/toolkit';

import { Subscription } from '../../records/Subscription';
import { State } from './reducers';
import {
  SUBSCRIPTION_STATE,
  TESTING_TYPES,
  PRODUCT_LIVE,
  PRODUCT_API_TESTING,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_RDC,
  PRODUCT_VDC,
  PRODUCT_USAGE,
  REAL_DEVICE,
  USAGE_TYPE,
  UNIFIED_BILLING_TYPE,
  VIRTUAL_DEVICE,
  CONCURRENCY_KEY,
  INTERVAL_KEY,
  PROJECTS_KEY,
  CREDIT_BLOCKS_KEY,
} from '../../constants';
import { getStartingPlansSelector } from '../plans/selectors';

const isLiveSubscription = (subscription: Subscription) => (
  subscription.plan.testing_type === TESTING_TYPES.LIVE
);
const isRdcSubscription = (subscription: Subscription) => (
  subscription.plan.device_type === REAL_DEVICE
);
const isVdcSubscription = (subscription: Subscription) => (
  subscription.plan.device_type === VIRTUAL_DEVICE
);
const isMobileQualitySubscription = (subscription: Subscription) => (
  subscription.plan.testing_type === TESTING_TYPES.MOBILE_QUALITY
);
const isApiTestingSubscription = (subscription: Subscription) => (
  subscription.plan.testing_type === TESTING_TYPES.API_TESTING
);
const isUsageSubscription = (subscription: Subscription) => (
  subscription.plan.plan_type === USAGE_TYPE
);
const isConcurrencySubscription = (subscription: Subscription) => (
  subscription.plan.plan_type !== USAGE_TYPE
);
const isUnifiedSubscriptions = (subscription: Subscription) => (
  subscription.plan.plan_type === UNIFIED_BILLING_TYPE
);
const isLegacySubscription = (subscription: Subscription) => (
  ![USAGE_TYPE, UNIFIED_BILLING_TYPE].includes(subscription.plan.plan_type)
);

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
const getStore = (store: any): State => store.apps.billing.subscriptions as State;

const getSubscriptionsSelector = createSelector(getStore, (store) => store.elements);

const hasBillingData = createSelector(
  getStore,
  (store) => store.isLoading || !!store.elements.size,
);

const getActiveSubscriptionsSelector = createSelector(
  getSubscriptionsSelector,
  (subscriptions) => subscriptions?.filter((subscription) => (
    subscription?.state === SUBSCRIPTION_STATE.ACTIVE
  )),
);

const currentMonthlyRateSelector = createSelector(
  getSubscriptionsSelector,
  (subscriptions) => subscriptions
    ?.reduce((acc: number, subscription) => acc + (subscription?.getMonthlyPrice() || 0), 0),
);

const getProductsFromSubscriptionSelector = createSelector(
  getStore,
  (store) => {
    const subscriptions = store.elements;
    const liveSubscription = subscriptions.find(isLiveSubscription);
    const rdcSubscription = subscriptions.find(isRdcSubscription);
    const vdcSubscription = subscriptions.find(isVdcSubscription);
    const mobileQualitySubscription = subscriptions.find(isMobileQualitySubscription);
    const apiTestingSubscription = subscriptions.find(isApiTestingSubscription);
    const usageSubscription = subscriptions.find(isUsageSubscription);

    return {
      ...(liveSubscription && {
        [PRODUCT_LIVE]: {
          [CONCURRENCY_KEY]: liveSubscription.getAddonAmount(),
          [INTERVAL_KEY]: liveSubscription.getInterval(),
        },
      }),
      ...(rdcSubscription && {
        [PRODUCT_RDC]: {
          [CONCURRENCY_KEY]: rdcSubscription.getAddonAmount(),
          [INTERVAL_KEY]: rdcSubscription.getInterval(),
        },
      }),
      ...(vdcSubscription && {
        [PRODUCT_VDC]: {
          [CONCURRENCY_KEY]: vdcSubscription.getAddonAmount(),
          [INTERVAL_KEY]: vdcSubscription.getInterval(),
        },
      }),
      ...(mobileQualitySubscription && {
        [PRODUCT_MOBILE_QUALITY]: {
          [CONCURRENCY_KEY]: mobileQualitySubscription.getAddonAmount(),
          [INTERVAL_KEY]: mobileQualitySubscription.getInterval(),
        },
      }),
      ...(apiTestingSubscription && {
        [PRODUCT_API_TESTING]: {
          [PROJECTS_KEY]: apiTestingSubscription.getAddonAmount(),
          [INTERVAL_KEY]: apiTestingSubscription.getInterval(),
        },
      }),
      ...(usageSubscription && {
        [PRODUCT_USAGE]: {
          [CREDIT_BLOCKS_KEY]: usageSubscription.getAddonAmount(),
          [INTERVAL_KEY]: usageSubscription.getInterval(),
        },
      }),
    };
  },
);

const hasActiveSubscriptionsSelector = createSelector(
  getActiveSubscriptionsSelector,
  (subscriptions) => subscriptions?.size > 0,
);

const getLegacySubscriptionsSelector = createSelector(
  getSubscriptionsSelector,
  (subscriptions) => subscriptions.filter(isLegacySubscription),
);

const getUnifiedSubscriptionsSelector = createSelector(
  getSubscriptionsSelector,
  (subscriptions) => {
    const unifiedSubscriptions = subscriptions.filter(isUnifiedSubscriptions);
    const liveSubscription = unifiedSubscriptions.find(isLiveSubscription);
    const rdcSubscription = unifiedSubscriptions.find(isRdcSubscription);
    const vdcSubscription = unifiedSubscriptions.find(isVdcSubscription);
    const mobileQualitySubscription = unifiedSubscriptions.find(isMobileQualitySubscription);
    const apiTestingSubscription = unifiedSubscriptions.find(isApiTestingSubscription);

    return [
      ...(liveSubscription ? [liveSubscription] : []),
      ...(vdcSubscription ? [vdcSubscription] : []),
      ...(rdcSubscription ? [rdcSubscription] : []),
      ...(mobileQualitySubscription ? [mobileQualitySubscription] : []),
      ...(apiTestingSubscription ? [apiTestingSubscription] : []),
    ];
  },
);

const getProposedPlansSelector = createSelector(
  getSubscriptionsSelector, getStartingPlansSelector,
  (subscriptions, plans) => ({
    live: subscriptions.some(isLiveSubscription) ? null : plans.live,
    automated: subscriptions.some(
      (sub: Subscription) => isVdcSubscription(sub) || isRdcSubscription(sub)) ?
      null : plans.automated,
    mobileQuality: subscriptions.some(isMobileQualitySubscription) ?
      null : plans.mobileQuality,
    apiTesting: subscriptions.some(isApiTestingSubscription) ?
      null : plans.apiTesting,
  }),

);

const getUsageSubscriptionsSelector = createSelector(
  getSubscriptionsSelector,
  (subscriptions) => subscriptions.filter(isUsageSubscription),
);

const geConcurrencySubscriptionsSelector = createSelector(
  getSubscriptionsSelector,
  (subscriptions) => subscriptions.filter(isConcurrencySubscription),
);

const getUnifiedAndUsageSubscriptionsSelector = createSelector(
  getUnifiedSubscriptionsSelector,
  getUsageSubscriptionsSelector,
  (unified, usage) => [
    ...unified,
    ...usage.toArray(),
  ]);

const isLoading = createSelector(getStore, (store) => store.isLoading);
const isAutoTopupEnabled = createSelector(getStore, (store) => store.autoRefillEnabled);

export {
  currentMonthlyRateSelector,
  geConcurrencySubscriptionsSelector,
  getActiveSubscriptionsSelector,
  getLegacySubscriptionsSelector,
  getProductsFromSubscriptionSelector,
  getProposedPlansSelector,
  getSubscriptionsSelector,
  getUnifiedAndUsageSubscriptionsSelector,
  getUnifiedSubscriptionsSelector,
  getUsageSubscriptionsSelector,
  hasActiveSubscriptionsSelector,
  hasBillingData,
  isLoading,
  isAutoTopupEnabled,
  isUsageSubscription,
};
